/**
 * Help Center - Main modal component with search and articles
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Search, 
  ChevronRight, 
  ChevronLeft,
  ExternalLink,
  Rocket,
  Folder,
  Database,
  FileText,
  MessageCircle,
  Link,
  Clock,
  Settings,
  HelpCircle,
  Puzzle
} from 'lucide-react';
import { HelpArticle } from './HelpArticle';
import { HelpSearch } from './HelpSearch';
import { 
  HelpArticleData, 
  HelpCategory, 
  HELP_CATEGORIES,
  HelpCategoryInfo 
} from './types';
import { 
  defaultHelpContent, 
  getArticlesByCategory, 
  searchArticles,
  getArticleById 
} from './helpContent';
import {
  RFP_AGENT_FEATURES,
  CORE_FEATURES,
  getAllFeatureCategories,
  groupArticlesByFeature,
  groupArticlesBySubcategory,
  getFeatureCategoryForArticle,
  calculateReadingTime,
  type FeatureCategory
} from './featureCategories';

export interface HelpCenterProps {
  isOpen: boolean;
  onClose: () => void;
  customArticles?: HelpArticleData[];
  supportEmail?: string;
  supportUrl?: string;
}

// Icon mapping for categories
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'rocket': Rocket,
  'folder': Folder,
  'database': Database,
  'file-text': FileText,
  'message-circle': MessageCircle,
  'link': Link,
  'clock': Clock,
  'settings': Settings,
  'help-circle': HelpCircle,
  'puzzle': Puzzle,
};

export function HelpCenter({ 
  isOpen, 
  onClose, 
  customArticles = [],
  supportEmail = 'support@alpyne.ai',
  supportUrl
}: HelpCenterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticleData | null>(null);
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Separate plugin articles from core articles for prioritized display
  // Filter out "coming soon" articles and prioritize key articles
  const { pluginArticles, coreArticles, allArticles } = useMemo(() => {
    const plugin: HelpArticleData[] = [];
    const core: HelpArticleData[] = [];
    
    // Helper to check if article is "coming soon"
    const isComingSoon = (article: HelpArticleData): boolean => {
      return article.comingSoon === true || 
             article.title.toLowerCase().includes('coming soon');
    };
    
    // Separate custom articles (from plugins) from default articles
    // Filter out coming soon articles
    customArticles.forEach(article => {
      if (isComingSoon(article)) return; // Skip coming soon articles
      
      if (article.plugin) {
        plugin.push(article);
      } else {
        plugin.push(article); // Still treat as plugin priority if in customArticles
      }
    });
    
    // Default articles are core (also filter coming soon)
    defaultHelpContent.forEach(article => {
      if (!isComingSoon(article)) {
        core.push(article);
      }
    });
    
    // Sort by priority (higher priority first), then by title
    const sortByPriority = (a: HelpArticleData, b: HelpArticleData) => {
      const priorityA = a.priority || 0;
      const priorityB = b.priority || 0;
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // Higher priority first
      }
      return a.title.localeCompare(b.title);
    };
    
    plugin.sort(sortByPriority);
    core.sort(sortByPriority);
    
    // Combined: plugin first, then core
    const combined = [...plugin, ...core];
    
    return {
      pluginArticles: plugin,
      coreArticles: core,
      allArticles: combined
    };
  }, [customArticles]);

  // Filter articles based on search, category, or feature, with plugin articles first
  const filteredArticles = useMemo(() => {
    let results: HelpArticleData[] = [];
    
    if (searchQuery) {
      const pluginResults = searchArticles(searchQuery, pluginArticles);
      const coreResults = searchArticles(searchQuery, coreArticles);
      results = [...pluginResults, ...coreResults];
    } else if (selectedFeature) {
      // Filter by feature category
      const allArticlesForFilter = [...pluginArticles, ...coreArticles];
      results = allArticlesForFilter.filter(article => {
        const feature = getFeatureCategoryForArticle(article);
        return feature?.id === selectedFeature;
      });
    } else if (selectedCategory) {
      const pluginResults = getArticlesByCategory(selectedCategory, pluginArticles);
      const coreResults = getArticlesByCategory(selectedCategory, coreArticles);
      results = [...pluginResults, ...coreResults];
    }
    
    return results;
  }, [searchQuery, selectedCategory, selectedFeature, pluginArticles, coreArticles]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (selectedArticle) {
          handleBack();
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedArticle, onClose]);

  // Navigation functions
  const handleCategorySelect = useCallback((category: HelpCategory) => {
    setSelectedCategory(category);
    setSelectedFeature(null);
    setSelectedArticle(null);
    setSearchQuery('');
  }, []);

  const handleFeatureSelect = useCallback((featureId: string) => {
    setSelectedFeature(featureId);
    setSelectedCategory(null);
    setSelectedArticle(null);
    setSearchQuery('');
  }, []);

  const handleArticleSelect = useCallback((article: HelpArticleData) => {
    setNavigationHistory(prev => [...prev, selectedArticle?.id || 'home']);
    setSelectedArticle(article);
  }, [selectedArticle]);
  
  // Reset scroll position when article changes
  useEffect(() => {
    if (!selectedArticle) return;
    
    const scrollToTop = () => {
      // Use the ref first (most reliable)
      if (contentScrollRef.current) {
        contentScrollRef.current.scrollTop = 0;
      }
      
      // Also try the outer modal container
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    };
    
    // Scroll immediately
    scrollToTop();
    
    // Scroll after brief delays to catch DOM updates
    const timeout1 = setTimeout(scrollToTop, 0);
    const timeout2 = setTimeout(scrollToTop, 10);
    const timeout3 = setTimeout(scrollToTop, 50);
    const timeout4 = setTimeout(scrollToTop, 100);
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
    };
  }, [selectedArticle?.id]); // Re-run when article ID changes

  const handleBack = useCallback(() => {
    if (selectedArticle) {
      const prevId = navigationHistory.pop();
      setNavigationHistory([...navigationHistory]);
      
      if (prevId && prevId !== 'home') {
        const prevArticle = getArticleById(prevId, allArticles);
        setSelectedArticle(prevArticle || null);
      } else {
        setSelectedArticle(null);
      }
    } else if (selectedFeature) {
      setSelectedFeature(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  }, [selectedArticle, selectedCategory, selectedFeature, navigationHistory, allArticles]);

  const handleHome = useCallback(() => {
    setSelectedArticle(null);
    setSelectedCategory(null);
    setSelectedFeature(null);
    setSearchQuery('');
    setNavigationHistory([]);
  }, []);

  const handleRelatedArticleClick = useCallback((articleId: string) => {
    // Search in all articles (plugin + core)
    const article = getArticleById(articleId, allArticles);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[HelpCenter] handleRelatedArticleClick:', {
        articleId,
        found: !!article,
        allArticlesCount: allArticles.length,
        articleTitle: article?.title
      });
    }
    
    if (article) {
      // Prevent circular navigation (don't navigate to the same article)
      if (selectedArticle && selectedArticle.id === articleId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[HelpCenter] Skipping navigation to same article:', articleId);
        }
        return;
      }
      
      // Add current article to navigation history
      if (selectedArticle) {
        setNavigationHistory(prev => [...prev, selectedArticle.id]);
      }
      
      // Immediately scroll to top before navigation
      const scrollToTop = () => {
        // Try multiple selectors to find the scrollable container
        const selectors = [
          '.fixed.inset-0.overflow-y-auto', // Main modal container
          '[class*="overflow-y-auto"]', // Any overflow container
          '.help-article-content', // Article content container
        ];
        
        for (const selector of selectors) {
          const element = document.querySelector(selector);
          if (element) {
            element.scrollTo({ top: 0, behavior: 'instant' });
            break;
          }
        }
        
        // Also try window scroll (fallback)
        window.scrollTo({ top: 0, behavior: 'instant' });
      };
      
      // Scroll immediately
      scrollToTop();
      
      // Also scroll after a short delay to ensure it works after DOM update
      setTimeout(scrollToTop, 0);
      setTimeout(scrollToTop, 50);
      setTimeout(scrollToTop, 100);
      
      // Navigate to the linked article
      handleArticleSelect(article);
      
      // Scroll after navigation (useEffect will also handle this, but this ensures it)
      setTimeout(() => {
        if (contentScrollRef.current) {
          contentScrollRef.current.scrollTop = 0;
        }
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 0);
    } else {
      // Article not found - log for debugging
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[HelpCenter] Article not found: ${articleId}`, {
          availableIds: allArticles.map(a => a.id).slice(0, 20),
          totalArticles: allArticles.length,
          sampleIds: allArticles.filter(a => a.id.includes('rfp-')).slice(0, 10).map(a => a.id)
        });
      }
      // Show user-friendly error (could add toast notification here)
    }
  }, [allArticles, handleArticleSelect, selectedArticle]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const canGoBack = selectedArticle !== null || selectedCategory !== null || selectedFeature !== null;

  const modalContent = (
    <div 
      ref={scrollContainerRef}
      className="fixed inset-0 z-[9999] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-center-title"
    >
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop - Full screen coverage */}
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal - Centered Container */}
        <div 
          className="relative w-full max-w-4xl max-h-[calc(100vh-2rem)] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto animate-in fade-in zoom-in-95 duration-200 z-50"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            {canGoBack && (
              <button
                onClick={handleBack}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </button>
            )}
            <div>
              <h2 
                id="help-center-title" 
                className="text-lg font-semibold text-slate-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                onClick={handleHome}
              >
                Help Center
              </h2>
              {selectedFeature && !selectedArticle && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {getAllFeatureCategories().find(f => f.id === selectedFeature)?.name}
                </p>
              )}
              {selectedCategory && !selectedArticle && !selectedFeature && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {HELP_CATEGORIES.find(c => c.id === selectedCategory)?.name}
                </p>
              )}
              {selectedArticle && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedArticle.title}
                </p>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            aria-label="Close Help Center"
          >
            <X className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </button>
        </div>

        {/* Search Bar */}
        {!selectedArticle && (
          <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-700">
            <HelpSearch
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
            />
          </div>
        )}

        {/* Content */}
        <div ref={contentScrollRef} className="flex-1 overflow-y-auto">
          {selectedArticle ? (
            // Article View
            <HelpArticle 
              key={selectedArticle.id} // Force re-render on article change
              article={selectedArticle}
              onRelatedClick={handleRelatedArticleClick}
              allArticles={allArticles}
            />
          ) : searchQuery ? (
            // Search Results - Plugin articles first, then core
            <div className="p-6">
              {(() => {
                const pluginResults = filteredArticles.filter(a => pluginArticles.includes(a));
                const coreResults = filteredArticles.filter(a => coreArticles.includes(a));
                const totalResults = filteredArticles.length;
                
                return (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                      {totalResults} result{totalResults !== 1 ? 's' : ''} for "{searchQuery}"
                    </p>
                    {totalResults > 0 ? (
                      <div className="space-y-4">
                        {/* Plugin Articles Section */}
                        {pluginResults.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <Puzzle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Plugin Articles ({pluginResults.length})
                              </h3>
                            </div>
                            <div className="space-y-2">
                              {pluginResults.map(article => (
                                <ArticleListItem
                                  key={article.id}
                                  article={article}
                                  onClick={() => handleArticleSelect(article)}
                                  isPlugin={true}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Core Articles Section */}
                        {coreResults.length > 0 && (
                          <div>
                            {pluginResults.length > 0 && (
                              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <HelpCircle className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                    Core Platform Articles ({coreResults.length})
                                  </h3>
                                </div>
                              </div>
                            )}
                            <div className="space-y-2">
                              {coreResults.map(article => (
                                <ArticleListItem
                                  key={article.id}
                                  article={article}
                                  onClick={() => handleArticleSelect(article)}
                                  isPlugin={false}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <HelpCircle className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-600 dark:text-slate-400">
                          No articles found for "{searchQuery}"
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                          Try different keywords or browse categories below
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : selectedCategory ? (
            // Category View - Plugin articles first, then core
            <div className="p-6">
              {(() => {
                const pluginResults = filteredArticles.filter(a => pluginArticles.includes(a));
                const coreResults = filteredArticles.filter(a => coreArticles.includes(a));
                
                return filteredArticles.length > 0 ? (
                  <div className="space-y-4">
                    {/* Plugin Articles Section */}
                    {pluginResults.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Puzzle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Plugin Articles ({pluginResults.length})
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {pluginResults.map(article => (
                            <ArticleListItem
                              key={article.id}
                              article={article}
                              onClick={() => handleArticleSelect(article)}
                              isPlugin={true}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Core Articles Section */}
                    {coreResults.length > 0 && (
                      <div>
                        {pluginResults.length > 0 && (
                          <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <HelpCircle className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                Core Platform Articles ({coreResults.length})
                              </h3>
                            </div>
                          </div>
                        )}
                        <div className="space-y-2">
                          {coreResults.map(article => (
                            <ArticleListItem
                              key={article.id}
                              article={article}
                              onClick={() => handleArticleSelect(article)}
                              isPlugin={false}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-slate-500 dark:text-slate-400">
                      No articles in this category yet.
                    </p>
                  </div>
                );
              })()}
            </div>
          ) : selectedFeature ? (
            // Feature View - Show articles for selected feature
            <div className="p-6">
              {(() => {
                const feature = getAllFeatureCategories().find(f => f.id === selectedFeature);
                if (!feature) return null;
                
                const pluginResults = filteredArticles.filter(a => pluginArticles.includes(a));
                const coreResults = filteredArticles.filter(a => coreArticles.includes(a));
                
                return (
                  <div className="space-y-4">
                    {/* Feature Header */}
                    <div className="mb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <feature.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                          {feature.name}
                        </h2>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {feature.description}
                      </p>
                    </div>
                    
                    {/* Plugin Articles */}
                    {pluginResults.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Puzzle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            RFP Agent Articles ({pluginResults.length})
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {pluginResults.map(article => (
                            <ArticleListItem
                              key={article.id}
                              article={article}
                              onClick={() => handleArticleSelect(article)}
                              isPlugin={true}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Core Articles */}
                    {coreResults.length > 0 && (
                      <div>
                        {pluginResults.length > 0 && (
                          <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <HelpCircle className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                Core Platform Articles ({coreResults.length})
                              </h3>
                            </div>
                          </div>
                        )}
                        <div className="space-y-2">
                          {coreResults.map(article => (
                            <ArticleListItem
                              key={article.id}
                              article={article}
                              onClick={() => handleArticleSelect(article)}
                              isPlugin={false}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {filteredArticles.length === 0 && (
                      <div className="text-center py-12">
                        <p className="text-slate-500 dark:text-slate-400">
                          No articles available for this feature yet.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            // Home View - Feature-based categories matching HomeDashboard
            <div className="p-6">
              <div className="space-y-6">
                {/* RFP Agent Features Section - Primary, Prominent */}
                {pluginArticles.length > 0 && (() => {
                  const groupedByFeature = groupArticlesByFeature(pluginArticles);
                  const rfpFeatures = RFP_AGENT_FEATURES.filter(f => {
                    const articles = groupedByFeature.get(f.id) || [];
                    return articles.length > 0;
                  });
                  
                  return rfpFeatures.length > 0 ? (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Puzzle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                          RFP Agent
                        </h2>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          ({pluginArticles.length} articles)
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {rfpFeatures.map(feature => {
                          const articles = groupedByFeature.get(feature.id) || [];
                          return (
                            <FeatureCategoryCard
                              key={feature.id}
                              feature={feature}
                              articleCount={articles.length}
                              onClick={() => handleFeatureSelect(feature.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : null;
                })()}
                
                {/* Core Features Section - Secondary */}
                {coreArticles.length > 0 && (() => {
                  const groupedByFeature = groupArticlesByFeature(coreArticles);
                  const coreFeatures = CORE_FEATURES.filter(f => {
                    const articles = groupedByFeature.get(f.id) || [];
                    return articles.length > 0;
                  });
                  
                  return coreFeatures.length > 0 ? (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <HelpCircle className="h-5 w-5 text-slate-500 dark:text-slate-400" />
                        <h2 className="text-lg font-semibold text-slate-600 dark:text-slate-400">
                          Core Features
                        </h2>
                        <span className="text-sm text-slate-400 dark:text-slate-500">
                          ({coreArticles.length} articles)
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {coreFeatures.map(feature => {
                          const articles = groupedByFeature.get(feature.id) || [];
                          return (
                            <FeatureCategoryCard
                              key={feature.id}
                              feature={feature}
                              articleCount={articles.length}
                              onClick={() => handleFeatureSelect(feature.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Can't find what you're looking for?
            </p>
            <div className="flex items-center gap-4">
              {supportUrl && (
                <a
                  href={supportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  Documentation
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <a
                href={`mailto:${supportEmail}`}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to body to avoid z-index and positioning issues
  // This ensures the modal is always rendered at the top level, regardless of parent container constraints
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(modalContent, document.body);
}

// Category Card Component
interface CategoryCardProps {
  category: HelpCategoryInfo;
  articleCount: number;
  onClick: () => void;
  isPlugin?: boolean;
}

function CategoryCard({ category, articleCount, onClick, isPlugin = false }: CategoryCardProps) {
  const Icon = categoryIcons[category.icon] || HelpCircle;

  return (
    <button
      onClick={onClick}
      className="
        flex items-start gap-4 p-4 rounded-xl
        bg-white dark:bg-slate-700/50
        border border-slate-200 dark:border-slate-600
        hover:border-blue-300 dark:hover:border-blue-500
        hover:shadow-md
        transition-all duration-200
        text-left group
      "
    >
      <div className="
        p-2.5 rounded-lg
        bg-blue-50 dark:bg-blue-900/30
        text-blue-600 dark:text-blue-400
        group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50
        transition-colors
      ">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ${
              isPlugin 
                ? 'text-slate-900 dark:text-white' 
                : 'text-slate-600 dark:text-slate-400'
            }`}>
              {category.name}
            </h3>
            {isPlugin && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                <Puzzle className="h-3 w-3" />
              </span>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
        </div>
        <p className={`text-sm mt-1 ${
          isPlugin 
            ? 'text-slate-500 dark:text-slate-400' 
            : 'text-slate-400 dark:text-slate-500'
        }`}>
          {category.description}
        </p>
        <p className={`text-xs mt-2 ${
          isPlugin 
            ? 'text-slate-400 dark:text-slate-500' 
            : 'text-slate-400 dark:text-slate-600'
        }`}>
          {articleCount} article{articleCount !== 1 ? 's' : ''}
        </p>
      </div>
    </button>
  );
}

// Feature Category Card Component
interface FeatureCategoryCardProps {
  feature: FeatureCategory;
  articleCount: number;
  onClick: () => void;
}

function FeatureCategoryCard({ feature, articleCount, onClick }: FeatureCategoryCardProps) {
  const Icon = feature.icon;

  return (
    <button
      onClick={onClick}
      className="
        flex items-start gap-4 p-4 rounded-xl
        bg-white dark:bg-slate-700/50
        border border-slate-200 dark:border-slate-600
        hover:border-blue-300 dark:hover:border-blue-500
        hover:shadow-md
        transition-all duration-200
        text-left group
      "
    >
      <div className="
        p-2.5 rounded-lg
        bg-blue-50 dark:bg-blue-900/30
        text-blue-600 dark:text-blue-400
        group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50
        transition-colors
      ">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {feature.name}
            </h3>
            {feature.section === 'rfp-agent' && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                <Puzzle className="h-3 w-3" />
              </span>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {feature.description}
        </p>
        {feature.subFeatures && feature.subFeatures.length > 0 && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            {feature.subFeatures.join(' • ')}
          </p>
        )}
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
          {articleCount} article{articleCount !== 1 ? 's' : ''}
        </p>
      </div>
    </button>
  );
}

// Article List Item Component
interface ArticleListItemProps {
  article: HelpArticleData;
  onClick: () => void;
  isPlugin?: boolean;
}

function ArticleListItem({ article, onClick, isPlugin = false }: ArticleListItemProps) {
  const category = HELP_CATEGORIES.find(c => c.id === article.category);
  const pluginName = article.plugin || (isPlugin ? 'Plugin' : null);
  const readingTime = calculateReadingTime(article.content);

  return (
    <button
      onClick={onClick}
      className="
        w-full flex items-center gap-4 p-4 rounded-lg
        bg-white dark:bg-slate-700/30
        border border-slate-200 dark:border-slate-600
        hover:border-blue-300 dark:hover:border-blue-500
        hover:shadow-sm
        transition-all duration-200
        text-left group
      "
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h4 className="font-medium text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {article.title}
          </h4>
          {isPlugin && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">
              <Puzzle className="h-3 w-3" />
              {pluginName === 'rfp-agent' ? 'RFP Agent' : pluginName}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
            <Clock className="h-3 w-3" />
            {readingTime} min read
          </span>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
          {article.summary}
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {category && (
            <span className="inline-block text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
              {category.name}
            </span>
          )}
          {article.subcategory && article.subcategory !== 'Other' && (
            <span className="inline-block text-xs text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-600">
              {article.subcategory}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-500 flex-shrink-0 transition-colors" />
    </button>
  );
}

